// SubscribeForm.tsx
// using OrderParamsForm
import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Paper,
    Checkbox,
    FormControlLabel,
    Stack,
} from "@mui/material";
import { api, Balance, BasePrice, CameraCatg } from "../api";
import { useFetch } from "../../../hooks/useFetch";
import InfoModal from '../components/InfoModal';
import { useTranslation } from 'react-i18next';
import { AgreementCheckbox } from "./AgreementCheckbox";
import { useOrderParams } from "./useOrderParams";
import { OrderParamsForm } from "./OrderParamsForm";
import { basename } from "../../../globals_VITE";

interface AnaliticType {
    type: 'Embedding' | 'Face Detection' | 'Demography';
    count: number;
}

interface OrderParam {
    periodMon: number;
    config: AnaliticType[];
}

interface Order {
    app_id: string;
    order_id: string;
    amount: number;
    currency: string;
    description: string;
    param: OrderParam;
    liqpayData?: object;
    dt: Date;
    type: 'liqpay'|'manual'|'bank';
}

interface SubscribeFormProps {
    onClose: () => void;
}

const SubscribeForm: React.FC<SubscribeFormProps> = ({ onClose }) => {
    const { t } = useTranslation();
    const [newEndDate, setNewEndDate] = useState<Date | undefined>(undefined);
    const [orderId, setOrderId] = useState(0);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const { data: cameraCatg, loading: cameraCatgLoading, error: cameraCatgError } = useFetch<CameraCatg[]>(api.getCameraCatg, []);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAgreementAccepted, setIsAgreementAccepted] = useState(false);
    const [paymentDone, setPaymentDone] = useState(false);
    const [kind, setKind] = useState<'offer' | 'privacy'>('offer');
    const { data: balance, } = useFetch<Balance | null>(api.getBalance, null);

    const orderParams = useOrderParams({initialPeriod: 1});
    const { currency, camerasAnalytics, camerasSimple, camerasAnalyticsFact, camerasSimpleFact, periodMon, setPeriodMon } = orderParams;

    const handleOpen = (kind: 'offer' | 'privacy') => {
        setIsModalOpen(true);
        setKind(kind);
    }

    const handleAccept = () => {
        setIsAgreementAccepted(true);
        setIsModalOpen(false);
    };
    const handleCancel = () => {
        setIsAgreementAccepted(false);
        setIsModalOpen(false);
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsAgreementAccepted(event.target.checked);
    };

    useEffect(() => {
        const startDate = balance?.endDate ? new Date(balance.endDate) : new Date();
        startDate.setMonth(startDate.getMonth() + 1);
        setNewEndDate(startDate);
    }, [balance]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!isAgreementAccepted) {
            alert(t('OrderForm.PleaseAcceptAgreement'));
            return;
        }

        if (camerasAnalyticsFact > camerasAnalytics) {
            alert(t('OrderForm.FactCamerasExceedAnalytics'));
            return;
        }

        if (camerasSimpleFact > camerasSimple) {
            alert(t('OrderForm.FactCamerasExceedSimple'));
            return;
        }

        const param = {
            periodMon, 
            camerasAnalytics,
            camerasSimple, 
            currency,
            pay_type: 'monobank'
        };

        const _orderId = 1;
        // const order = await api.createOrderMonobank(amount, currency, t('OrderForm.Description', { orderId: _orderId }), param, );
        const order = await api.postPreInvoice(param, );

        if (!!order && !order.activate) {
            alert(t('OrderForm.FailedToCreateOrder'));
        } else
        if (!!order) {
            const url = basename+`/api/billing/pay_monobank/?order_id=${order.order_id}`;
            window.open(url, '_blank');
            setSuccessMessage(t('OrderForm.PaymentStartedMessage')); 
            setIsChecking(true);
            setOrderId(order.order_id);
        } else {
            alert(t('OrderForm.FailedToCreateOrder'));
        }
    };

    useEffect(() => {
        if (!isChecking) return;
        const intervalId = setInterval(async () => {
            try {
                const data = await api.getOrderStatus(orderId);
                if (data.result === 'ok') {
                    setSuccessMessage(t('OrderForm.PaymentSuccessMessage', { orderId }));
                    setIsChecking(false);
                    clearInterval(intervalId);
                    setPaymentDone(true);
                    onClose();
                }
                if (data.err_description) {
                    setSuccessMessage(data.err_description);
                }
            } catch (err) {
                console.error(t('OrderForm.ErrorFetchingStatus'), err);
            }
        }, 5000);
        return () => clearInterval(intervalId);
    }, [isChecking, orderId, t, onClose]);

    return (
        <Paper elevation={3} sx={{ maxWidth: 420, mx: "auto", p: 3, mt: 4 }}>
            <form onSubmit={handleSubmit}>
                <Typography variant="h5" gutterBottom>
                    Підписка на регулярний платіж
                </Typography>

                <OrderParamsForm {...orderParams} hidePeriod={true}/>
                

                <AgreementCheckbox
                    checked={isAgreementAccepted}
                    onChange={(checked) => setIsAgreementAccepted(checked)}
                    onOpenOffer={() => handleOpen('offer')}
                    onOpenPrivacy={() => handleOpen('privacy')}
                />

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ fontWeight: "bold", px: 4 }}
                        disabled={!isAgreementAccepted || paymentDone}
                    >
                        {t('OrderForm.PayButton')}
                    </Button>
                    <Button
                        type="button"
                        onClick={onClose}
                        variant="outlined"
                        color="secondary"
                    >
                        {t('OrderForm.ExitButton')}
                    </Button>
                </Stack>

                {isChecking && !successMessage && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t('OrderForm.CheckingPayment')}
                    </Typography>
                )}
                {successMessage && (
                    <Typography variant="body1" color="success.main" sx={{ mt: 2 }}>
                        {successMessage}
                    </Typography>
                )}
            </form>
            
            <InfoModal
                open={isModalOpen}
                onCancel={handleCancel}
                onAccept={handleAccept}
                kind={kind}
            />
        </Paper>
    );
};

export default SubscribeForm;
